import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Twitter, Send, Globe, ExternalLink, ShieldCheck, Droplets, Users, MessageCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, shortAddr } from "@/lib/api";
import { normalizeSocialUrl } from "@/lib/socials";
import { useWallet } from "@/context/WalletContext";
import CoinLogo from "@/components/CoinLogo";
import BondingCurveBar from "@/components/BondingCurveBar";
const PriceChart = React.lazy(() => import("@/components/PriceChart"));
import TradePanel from "@/components/TradePanel";
import { fmtNum, fmtSui, fmtPrice, timeAgo } from "@/lib/format";

const TABS = ["Trades", "Holders", "Comments", "Cetus Pool"];

export default function CoinDetail() {
  const { id } = useParams();
  const { address, connected } = useWallet();
  const [coin, setCoin] = useState(null);
  const [chart, setChart] = useState([]);
  const [trades, setTrades] = useState([]);
  const [holders, setHolders] = useState([]);
  const [comments, setComments] = useState([]);
  const [holding, setHolding] = useState(null);
  const [tab, setTab] = useState("Trades");
  const [comment, setComment] = useState("");
  const prevPrice = useRef(null);
  const [flash, setFlash] = useState("");

  const loadCoin = useCallback(async () => {
    try {
      const { data } = await api.get(`/coins/${id}`);
      if (prevPrice.current !== null && data.price_sui !== prevPrice.current) {
        setFlash(data.price_sui > prevPrice.current ? "flash-green" : "flash-red");
        setTimeout(() => setFlash(""), 500);
      }
      prevPrice.current = data.price_sui;
      setCoin(data);
    } catch (e) {}
  }, [id]);

  const loadSub = useCallback(async () => {
    const [c, t, h, cm] = await Promise.all([
      api.get(`/coins/${id}/chart`),
      api.get(`/coins/${id}/trades`),
      api.get(`/coins/${id}/holders`),
      api.get(`/coins/${id}/comments`),
    ]);
    setChart(c.data); setTrades(t.data); setHolders(h.data); setComments(cm.data);
  }, [id]);

  const loadHolding = useCallback(async () => {
    if (!address) { setHolding(null); return; }
    try {
      const { data } = await api.get(`/portfolio/${address}`);
      setHolding(data.holdings.find((x) => x.coin_id === id) || { balance: 0 });
    } catch (e) {}
  }, [address, id]);

  useEffect(() => { loadCoin(); loadSub(); }, [loadCoin, loadSub]);
  useEffect(() => { loadHolding(); }, [loadHolding]);
  useEffect(() => {
    const t = setInterval(() => { loadCoin(); loadSub(); }, 5000);
    return () => clearInterval(t);
  }, [loadCoin, loadSub]);

  const onTraded = () => { loadCoin(); loadSub(); loadHolding(); };

  const postComment = async () => {
    if (!comment.trim()) return;
    if (!connected) return toast.error("Connect your Sui wallet first");
    try {
      await api.post(`/coins/${id}/comments`, { address, text: comment });
      setComment("");
      loadSub();
    } catch (e) { toast.error("Failed to post"); }
  };

  if (!coin) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-white mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to board
      </Link>

      {coin.migrated && (
        <div data-testid="cetus-migration-banner" className="rounded-xl p-4 mb-5 border border-[#00E5FF]/40 flex items-center gap-3" style={{ background: "linear-gradient(90deg,rgba(0,229,255,0.1),rgba(255,0,122,0.1))" }}>
          <Sparkles className="w-6 h-6 text-[#00E5FF]" />
          <div>
            <div className="font-display font-bold">🎉 Graduated to Cetus DEX</div>
            <div className="text-xs text-muted-foreground">7,500 SUI reached — liquidity migrated & LP burned. Trade on the Cetus AMM pool.</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left */}
        <div className="lg:col-span-8 space-y-5">
          {/* Header card */}
          <div className="card-surface p-5">
            <div className="flex items-start gap-4">
              <CoinLogo coin={coin} size={72} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="font-display font-extrabold text-2xl">{coin.name}</h1>
                  <span className="font-mono text-[#1EC62E] font-bold">${coin.symbol}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">{coin.description}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                  <span className="font-mono">by {shortAddr(coin.creator)}</span>
                  <span>{timeAgo(coin.created_at)}</span>
                  {coin.twitter && <a href={normalizeSocialUrl("twitter", coin.twitter)} target="_blank" rel="noreferrer" className="hover:text-[#1EC62E]"><Twitter className="w-4 h-4" /></a>}
                  {coin.telegram && <a href={normalizeSocialUrl("telegram", coin.telegram)} target="_blank" rel="noreferrer" className="hover:text-[#1EC62E]"><Send className="w-4 h-4" /></a>}
                  {coin.website && <a href={normalizeSocialUrl("website", coin.website)} target="_blank" rel="noreferrer" className="hover:text-[#1EC62E]"><Globe className="w-4 h-4" /></a>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider">Price</div>
                <div className={`font-mono font-bold ${flash}`}>{fmtPrice(coin.price_sui)}</div>
                <div className="text-[11px] text-muted-foreground mt-1">MC {fmtNum(coin.market_cap_sui)} SUI</div>
                {coin.first_trade_done && (
                  <div data-testid="dev-bought-first" className={`mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-bold uppercase tracking-wider ${coin.dev_bought_first ? "text-[#FFB800] bg-[#FFB800]/10 border border-[#FFB800]/30" : "text-muted-foreground bg-white/5 border border-white/10"}`}>
                    Dev bought first: {coin.dev_bought_first ? "YES" : "NO"}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bonding curve */}
          <div className="card-surface p-5">
            <BondingCurveBar progress={coin.progress} migrated={coin.migrated} current={coin.real_sui_collected} remaining={coin.sui_remaining} />
            <p className="text-[11px] text-muted-foreground mt-3">
              When the bonding curve reaches <b className="text-white">7,500 SUI</b>, liquidity auto-deploys to <b className="text-[#00E5FF]">Cetus</b>. Fee on each trade: <b className="text-[#1EC62E]">0.7% dev</b> + <b className="text-[#1EC62E]">0.3% platform</b>.
            </p>
          </div>

          {/* Chart */}
          <div className="card-surface p-5">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-2">Price Chart</div>
            <React.Suspense fallback={<div className="h-[300px] flex items-center justify-center text-muted-foreground text-sm">Loading chart…</div>}>
              <PriceChart data={chart} />
            </React.Suspense>
          </div>

          {/* Tabs */}
          <div className="card-surface p-5">
            <div className="flex items-center gap-1 mb-4 border-b border-white/10">
              {TABS.map((t) => (
                <button key={t} data-testid={`tab-${t.replace(/\s/g, "-").toLowerCase()}`} onClick={() => setTab(t)}
                  className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === t ? "border-[#1EC62E] text-white" : "border-transparent text-muted-foreground hover:text-white"}`}>
                  {t}
                </button>
              ))}
            </div>

            {tab === "Trades" && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs font-mono">
                  <thead><tr className="text-muted-foreground text-left"><th className="py-1.5">Account</th><th>Type</th><th className="text-right">SUI</th><th className="text-right">{coin.symbol}</th><th className="text-right">Time</th></tr></thead>
                  <tbody>
                    {trades.map((t) => (
                      <tr key={t.id} className="border-t border-white/5">
                        <td className="py-2">{shortAddr(t.address)}</td>
                        <td><span className={t.side === "buy" ? "text-[#22C55E]" : "text-[#F43F5E]"}>{t.side}</span></td>
                        <td className="text-right">{fmtSui(t.sui_amount, 3)}</td>
                        <td className="text-right">{fmtNum(t.token_amount)}</td>
                        <td className="text-right text-muted-foreground">{timeAgo(t.ts)}</td>
                      </tr>
                    ))}
                    {trades.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No trades yet</td></tr>}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "Holders" && (
              <div className="space-y-1.5">
                {holders.map((h, i) => (
                  <div key={h.address} className="flex items-center justify-between text-xs font-mono py-1.5 border-b border-white/5">
                    <span className="text-muted-foreground">{i + 1}. {shortAddr(h.address)}{h.address === coin.creator && <span className="ml-2 text-[#FFB800]">dev</span>}</span>
                    <div className="flex items-center gap-3"><span>{fmtNum(h.balance)}</span><span className="text-[#1EC62E] w-14 text-right">{h.pct.toFixed(2)}%</span></div>
                  </div>
                ))}
                {holders.length === 0 && <div className="py-6 text-center text-muted-foreground text-sm">No holders yet</div>}
              </div>
            )}

            {tab === "Comments" && (
              <div>
                <div className="flex gap-2 mb-4">
                  <input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && postComment()}
                    placeholder="Post a comment..." data-testid="comment-input"
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1EC62E]/50" />
                  <button onClick={postComment} data-testid="comment-submit" className="px-4 py-2 rounded-lg font-bold text-sm text-black" style={{ background: "linear-gradient(90deg,#1EC62E,#7CF24E)" }}>Post</button>
                </div>
                <div className="space-y-3">
                  {comments.map((c) => (
                    <div key={c.id} className="text-sm">
                      <span className="font-mono text-xs text-[#1EC62E]">{shortAddr(c.address)}</span>
                      <span className="text-muted-foreground text-xs ml-2">{timeAgo(c.ts)}</span>
                      <p className="mt-0.5">{c.text}</p>
                    </div>
                  ))}
                  {comments.length === 0 && <div className="py-6 text-center text-muted-foreground text-sm">No comments. Say something!</div>}
                </div>
              </div>
            )}

            {tab === "Cetus Pool" && (
              <div>
                {coin.migrated && coin.cetus_pool ? (
                  <div className="space-y-3 text-sm font-mono">
                    <div className="flex items-center gap-2 text-[#00E5FF]"><Droplets className="w-4 h-4" /> Cetus AMM Pool (live)</div>
                    <Row label="Pool ID" value={shortAddr(coin.cetus_pool.pool_id)} />
                    <Row label="SUI liquidity" value={`${fmtSui(coin.cetus_pool.sui_liquidity)} SUI`} />
                    <Row label={`${coin.symbol} liquidity`} value={fmtNum(coin.cetus_pool.token_liquidity)} />
                    <Row label="Fee tier" value={coin.cetus_pool.fee_tier} />
                    <Row label="LP tokens" value={<span className="text-[#1EC62E] flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Burned</span>} />
                    <a href={coin.cetus_pool.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-2 px-4 py-2 rounded-lg font-bold text-black" style={{ background: "linear-gradient(90deg,#00E5FF,#FF007A)" }}>
                      Open on Cetus <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                ) : (
                  <div className="py-6 text-center text-muted-foreground text-sm">
                    Not migrated yet. Pool goes live on Cetus once the bonding curve hits 7,500 SUI.
                    <div className="mt-2 text-xs">({fmtSui(coin.sui_remaining)} SUI remaining)</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right sticky trade */}
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-24 space-y-4">
            <TradePanel coin={coin} holding={holding} onTraded={onTraded} />
            {holding && holding.balance > 0 && (
              <div className="card-surface p-4 text-xs font-mono">
                <div className="text-muted-foreground uppercase tracking-wider mb-2">Your position</div>
                <div className="flex justify-between"><span>Balance</span><span>{fmtNum(holding.balance)} {coin.symbol}</span></div>
                <div className="flex justify-between mt-1"><span>Value</span><span>{fmtSui(holding.balance * coin.price_sui, 3)} SUI</span></div>
              </div>
            )}
            <div className="card-surface p-4 grid grid-cols-2 gap-3 text-xs font-mono">
              <Stat label="Holders" value={holders.length} />
              <Stat label="Volume" value={`${fmtSui(coin.volume_sui, 1)} SUI`} />
              <Stat label="Replies" value={coin.reply_count || 0} />
              <Stat label="Dev bought 1st" value={coin.first_trade_done ? (coin.dev_bought_first ? "YES" : "NO") : "—"} />
              <Stat label="Dev fees" value={`${fmtSui(coin.dev_fees_accrued, 2)}`} />
              <Stat label="Platform fees" value={`${fmtSui(coin.platform_fees_accrued, 2)}`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between border-b border-white/5 py-1.5"><span className="text-muted-foreground">{label}</span><span>{value}</span></div>;
}
function Stat({ label, value }) {
  return <div><div className="text-muted-foreground text-[10px] uppercase tracking-wider">{label}</div><div className="font-bold text-sm mt-0.5">{value}</div></div>;
}
